import { createTheme } from '@mui/material/styles';

import { FORTA_BLUE, FORTA_GRAY2, FORTA_GRAY3, FORTA_GRAY4, FORTA_WHITE } from './colors';
import { getThemeColor } from './common';
import SortImg from '../assets/images/sort.svg';

import type { Themes } from '../constants';

declare module '@mui/material/styles' {
  interface Components {
    MUIDataTableBodyRow?: object;
    MUIDataTableBodyCell?: object;
    MUIDataTableHeadCell?: object;
    MUIDataTableToolbar?: object;
    MUIDataTableFilter?: object;
  }
}

export const getTFHExcelTableTheme = (theme: Themes) =>
  createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: '8px',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            cursor: 'default',
            textAlign: 'left',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover': {
              '&:hover': {
                backgroundColor: FORTA_WHITE,
              },
            },
          },
        },
      },
      MUIDataTableBodyRow: {
        styleOverrides: {
          root: {
            borderBottom: `2px solid ${FORTA_GRAY2}`,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
            color: getThemeColor(theme).primary,
          },
          contentWrapper: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            '::after': {
              content: `""`,
              backgroundImage: `url(${SortImg})`,
              backgroundRepeat: 'no-repeat',
              width: '1.2em',
              height: '1.2em',
              display: 'block',
              marginTop: '0.5em',
              marginRight: '4px',
            },
          },
          sortAction: {
            '& path': {
              color: FORTA_BLUE.primary,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'left',
            fontSize: '14px !important',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontSize: '14px !important',
          },
          h6: {
            fontSize: '14px !important',
          },
        },
      },
    },
  });

export const primaryTheme = createTheme({
  palette: {
    primary: {
      main: FORTA_BLUE.primary,
      light: FORTA_BLUE.light3,
      dark: FORTA_BLUE.primary,
      contrastText: FORTA_WHITE,
    },
    secondary: {
      main: FORTA_BLUE.primary,
      light: FORTA_BLUE.light2,
      dark: FORTA_BLUE.primary,
      contrastText: FORTA_WHITE,
    },
  },
  typography: {
    fontFamily: ['DMSansRegular', 'DMSansBold', 'DMSansMedium'].join(','),
    h1: {
      fontSize: '54px',
      fontFamily: 'DMSansBold',
      lineHeight: '81px',
      letterSpacing: '-1px',
    },
    h2: {
      fontSize: '32px',
      fontFamily: 'DMSansBold',
      lineHeight: '54px',
      letterSpacing: '-1px',
    },
    h3: {
      fontSize: '24px',
      fontFamily: 'DMSansBold',
      lineHeight: '42px',
    },
    h4: {
      fontSize: '21px',
      fontFamily: 'DMSansMedium',
      lineHeight: '38px',
      letterSpacing: '-0.44px',
    },
    subtitle1: {
      fontSize: '24px',
      fontFamily: 'DMSansRegular',
      lineHeight: '38px',
      letterSpacing: '-0.44px',
    },
    button: {
      fontSize: '16px',
      fontFamily: 'DMSansBold',
      lineHeight: '21px',
      textTransform: 'uppercase',
    },
    overline: {
      fontSize: '14px',
      fontFamily: 'DMSansBold',
      lineHeight: '18px',
      color: FORTA_GRAY3,
    },
    body1: {
      fontSize: '16px',
      fontFamily: 'DMSansRegular',
      lineHeight: '24px',
    },
    body2: {
      fontSize: '18px',
      fontFamily: 'DMSansRegular',
      lineHeight: '27px',
    },
  },
});

export const multiplePaymentsTableTheme = createTheme({
  ...primaryTheme,
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: FORTA_WHITE,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          border: 'none',
          color: FORTA_BLUE.primary,
          fontWeight: 700,
          '&:first-of-type': {
            borderRadius: '8px',
          },
          '&:last-of-type': {
            borderRadius: '8px',
          },
          fontSize: '14px',
        },
        body: {
          borderTop: `0.5px solid ${FORTA_BLUE.light3}`,
          borderBottom: 'none',
          fontSize: '14px',
        },
        footer: {
          color: FORTA_BLUE.primary,
          fontWeight: 700,
          fontSize: '14px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: FORTA_WHITE,
          border: 'none',
        },
      },
    },
  },
});

export const serviceLinePaymentTableTheme = createTheme({
  ...primaryTheme,
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: FORTA_WHITE,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: FORTA_BLUE.primary,
          border: 'none',
          fontSize: '14px',
          minWidth: '130px',
        },
        root: {
          fontWeight: 700,
        },
        body: {
          fontSize: '14px',
          borderTop: `1px solid ${FORTA_BLUE.light2}`,
          '&:first-of-type': {
            borderLeft: `8px solid ${FORTA_BLUE.light2}`,
          },
          '&:last-child': {
            borderRight: `1px solid ${FORTA_BLUE.light2}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          backgroundColor: FORTA_WHITE,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: '14px !important',
        },
        h6: {
          fontSize: '14px !important',
        },
      },
    },
  },
});

export const correctClaimTableTheme = createTheme({
  ...primaryTheme,
  components: {
    MUIDataTableHeadCell: {
      styleOverrides: {
        contentWrapper: {
          '&:last-child': {
            display: 'block',
            textAlign: 'right',
          },
        },
      },
    },
  },
});

export const dataTableTheme = createTheme({
  ...primaryTheme,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#E7EDF1',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0 16px',
          padding: '0 24px',
          '& caption': {
            display: 'none',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.25)',
        },
        head: {
          borderRadius: 0,
          boxShadow: '0px 0px 0px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 8,
          textAlign: 'left',
          backgroundColor: '#FFFFFF',
          borderWidth: 0,
          color: FORTA_BLUE.primary,
          '&:first-of-type': {
            borderLeftStyle: 'solid',
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
          },
          '&:last-child': {
            borderRightStyle: 'solid',
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
          },
        },
        head: {
          color: FORTA_GRAY4,
          fontWeight: 700,
        },
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: '#E7EDF1',
          padding: '0 8px',
        },
        contentWrapper: {
          justifyContent: 'center',
        },
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {},
      },
    },
  },
});
